import React, { type ChangeEvent, useMemo } from "react";
import { type FormikErrors } from "formik";
import { type Config } from "kadow-jetpack-model";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkSquare } from "@fortawesome/free-solid-svg-icons";
import { tt } from "../../../../../utils/i18n/i18n";
import KC_Span from "../../../../../designsystem/KC_Span/KC_Span";
import KC_Colors from "../../../../../designsystem/KC_Colors";

interface DownloadAppLinkFieldsProps {
  idx: number;
  setFieldValue: (field: string, value: any) => void;
  errors: FormikErrors<Config>;
  config: Config;
}

const DownloadAppLinkFields: React.FC<DownloadAppLinkFieldsProps> = (
  props
) => {
  const goToLink = (link: string) => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const buildIOSUrl = (appStoreId: string): string => {
    return `https://apps.apple.com/app/id${appStoreId}`;
  };

  const buildAndroidUrl = (bundleId: string): string => {
    return `https://play.google.com/store/apps/details?id=${bundleId}`;
  };

  const setIOSFieldValue = (val: string) => {
    let formattedLink = val.trim();

    if (formattedLink.startsWith("http://")) {
      formattedLink = formattedLink.replace("http://", "https://");
    }

    if (!formattedLink.startsWith("https://")) {
      formattedLink = `https://${formattedLink}`;
    }

    const appStoreId = extractAppStoreId(formattedLink);

    if (appStoreId) {
      props.setFieldValue("socials.appStoreId", appStoreId);
    } else {
      props.setFieldValue("socials.appStoreId", "");
    }
  };

  const setAndroidFieldValue = (val: string) => {
    let formattedLink = val.trim();

    if (formattedLink.startsWith("http://")) {
      formattedLink = formattedLink.replace("http://", "https://");
    }

    if (!formattedLink.startsWith("https://")) {
      formattedLink = `https://${formattedLink}`;
    }

    const bundleId = extractAndroidBundleId(formattedLink);

    if (bundleId) {
      props.setFieldValue("socials.androidBundleId", bundleId);
    } else {
      props.setFieldValue("socials.androidBundleId", "");
    }
  };

  const iosUrl = useMemo(() => {
    const appStoreId = props.config.socials?.appStoreId;
    return appStoreId ? buildIOSUrl(appStoreId) : "";
  }, [props.config.socials?.appStoreId]);

  const androidUrl = useMemo(() => {
    const bundleId = props.config.socials?.androidBundleId;
    return bundleId ? buildAndroidUrl(bundleId) : "";
  }, [props.config.socials?.androidBundleId]);

  const isIOSValid = useMemo(() => {
    return !!props.config.socials?.appStoreId;
  }, [props.config.socials?.appStoreId]);

  const isAndroidValid = useMemo(() => {
    return !!props.config.socials?.androidBundleId;
  }, [props.config.socials?.androidBundleId]);

  const error = useMemo(() => {
    // @ts-ignore
    return props.errors?.ctas?.[props.idx]?.customAction;
  }, [props.errors, props.idx]);

  return (
    <div className="d-flex flex-column gap-sm">
      <div className="kc-input-wrapper">
        <input
          id="socials.appStoreId"
          className="form-control kc_input"
          type="text"
          value={iosUrl}
          placeholder={tt("cta_link_fields_placeholder_ios_appstore")}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setIOSFieldValue(e.target.value);
          }}
          required={true}
        />
        <div className="input-group-append p-2 border-left h-100 d-flex align-items-center gap-sm">
          <span style={{ fontSize: "18px" }}>{isIOSValid ? "✅" : "❌"}</span>
          <div
            className="cursor-pointer"
            onClick={() => {
              if (iosUrl) goToLink(iosUrl);
            }}
          >
            <FontAwesomeIcon
              icon={faExternalLinkSquare}
              color={"#FF2C81"}
              size={"lg"}
            />
          </div>
        </div>
      </div>

      <div className="kc-input-wrapper">
        <input
          id="socials.androidBundleId"
          className="form-control kc_input"
          type="text"
          value={androidUrl}
          placeholder={tt("cta_link_fields_placeholder_android_playstore")}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setAndroidFieldValue(e.target.value);
          }}
          required={true}
        />
        <div className="input-group-append p-2 border-left h-100 d-flex align-items-center gap-sm">
          <span style={{ fontSize: "18px" }}>
            {isAndroidValid ? "✅" : "❌"}
          </span>
          <div
            className="cursor-pointer"
            onClick={() => {
              if (androidUrl) goToLink(androidUrl);
            }}
          >
            <FontAwesomeIcon
              icon={faExternalLinkSquare}
              color={"#FF2C81"}
              size={"lg"}
            />
          </div>
        </div>
      </div>

      {!!error && (
        <KC_Span
          id={"error"}
          className="error"
          text={error}
          color={KC_Colors.Error}
        />
      )}
    </div>
  );
};

export default DownloadAppLinkFields;

const extractAppStoreId = (url: string): string | null => {
  const match = url.match(/id(\d+)/);
  return match ? match[1] : null;
};

const extractAndroidBundleId = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get("id");
  } catch {
    return null;
  }
};
